import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Truck, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-[var(--navy)]">
        <img src="https://images.unsplash.com/photo-1726866672851-5b99c837603c?crop=entropy&cs=srgb&fm=jpg&q=85" className="absolute inset-0 w-full h-full object-cover opacity-30" alt="logistics" />
        <div className="relative z-10 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2">
            <Truck size={22} className="text-amber-400" />
            <div className="font-heading font-extrabold text-xl">SRAD Dispatch Hub</div>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight">Forgot your password?<br/><span className="text-amber-400">No worries.</span></h1>
            <p className="text-sm text-slate-300 mt-4 max-w-md">Enter your registered email and we'll send a secure reset link. Valid for 60 minutes. Works for SRAD users and client users.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="forgot-form">
          <Link to="/login" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-6"><ArrowLeft size={12} /> Back to sign in</Link>
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Password Reset</div>
            <h2 className="font-heading text-3xl font-extrabold text-slate-900">Forgot Password</h2>
            <p className="text-sm text-slate-500 mt-2">We'll email you a reset link.</p>
          </div>

          {sent ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm text-emerald-800" data-testid="forgot-success">
              <div className="flex items-center gap-2 font-semibold"><MailCheck size={16} /> Reset link sent</div>
              <div className="text-xs mt-2">If <b>{email}</b> exists in our system, you'll receive an email shortly with a link to set a new password. The link is valid for 60 minutes.</div>
              <Link to="/login" className="inline-block mt-4 text-sm text-slate-900 border border-slate-300 rounded-md px-3 py-1.5 font-semibold">Back to sign in</Link>
            </div>
          ) : (
            <>
              {err && <div data-testid="forgot-error" className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Registered Email</label>
              <input data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-1 mb-6 px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" type="email" required autoFocus />
              <button data-testid="forgot-submit" disabled={loading} className="w-full bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold py-2.5 rounded-md disabled:opacity-60">
                {loading ? "Sending…" : "Send Reset Link"}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
